import {
  Component, input, output, signal, computed, inject, OnInit, OnDestroy
} from '@angular/core';
import { Router, RouterLink, RouterLinkActive, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

interface NavItem {
  id: string;
  label: string;
  icon: string;
  route: string;
  queryParams?: Record<string, string>;
  badge?: number;
  dividerBefore?: boolean;
  sectionLabel?: string;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent implements OnInit, OnDestroy {
  collapsed = input<boolean>(false);
  toggleCollapse = output<void>();

  private router = inject(Router);
  private navSub?: Subscription;

  currentLobId = signal<string | null>(null);

  globalNavItems: NavItem[] = [
    { id: 'lob-list',    label: 'Lines of Business', icon: '◈', route: '/line-of-business' },
    { id: 'algorithms',  label: 'Sub Parts',         icon: '⬡', route: '/sub-parts' },
    { id: 'csv-import', label: 'CSV Import',         icon: '⬆', route: '/csv-import',    dividerBefore: true },
    { id: 'audit',      label: 'Audit Logs',         icon: '☰', route: '/audit-logs',    dividerBefore: true },
    { id: 'deployment', label: 'Deployment',         icon: '⇧', route: '/deployment' },
  ];

  lobNavItems = computed<NavItem[]>(() => {
    const id = this.currentLobId();
    if (!id) return [];
    return [
      { id: 'input-variables', label: 'Input Variables', icon: '⊕', route: `/line-of-business/${id}/input-variables`, dividerBefore: true, sectionLabel: 'Build' },
      { id: 'lookup-tables',   label: 'Lookup Tables',   icon: '⊟', route: `/line-of-business/${id}/lookup-tables` },
      { id: 'variables',       label: 'Variables',       icon: '⊛', route: `/line-of-business/${id}/variables` },
      { id: 'formula',         label: 'Formula Editor',  icon: 'ƒ',  route: `/line-of-business/${id}/formula` },
      { id: 'canvas',          label: 'Rule Builder',    icon: '⬡', route: `/line-of-business/${id}/canvas` },
      // Endorsement Studio is hidden for now — see the note on its routes in
      // app.routes.ts. Restore this entry together with those routes.
      // { id: 'endorsements',    label: 'Endorsement Studio', icon: '⊜', route: `/line-of-business/${id}/endorsements` },
      { id: 'form-designer',   label: 'Form Designer',   icon: '⊡', route: `/line-of-business/${id}/form-designer`,   dividerBefore: true, sectionLabel: 'Test & Review' },
      { id: 'simulator',       label: 'Rating Simulator',icon: '▶', route: '/simulator',       queryParams: { algorithmId: id } },
      { id: 'run-algorithm',   label: 'Calculate Premium', icon: '⚡', route: '/run-algorithm', queryParams: { algorithmId: id } },
      { id: 'validation',      label: 'Validation',      icon: '✓', route: '/validation',      queryParams: { algorithmId: id } },
      { id: 'version',         label: 'Version Compare', icon: '⊙', route: '/version-compare', queryParams: { algorithmId: id } },
    ];
  });

  ngOnInit(): void {
    this.extractLobId(this.router.url);
    this.navSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => this.extractLobId(e.urlAfterRedirects));
  }

  ngOnDestroy(): void {
    this.navSub?.unsubscribe();
  }

  private extractLobId(url: string): void {
    // Match /line-of-business/:id/... (any sub-route) — but NOT the two
    // LOB-level screens (algorithms list, LOB's own rule-builder), where
    // :id is a LineOfBusinessId, not an AlgorithmId, so these Algorithm-scoped
    // links (canvas/variables/lookup-tables/etc.) would resolve against the
    // wrong entity.
    const pathMatch = url.match(/^\/line-of-business\/([^\/\?#]+)(?:\/([^\/\?#]+))?/);
    if (pathMatch && pathMatch[2] !== 'sub-parts' && pathMatch[2] !== 'algorithms'
        && pathMatch[2] !== 'rule-builder') {
      this.currentLobId.set(pathMatch[1]);
      return;
    }

    // For global routes that carry algorithmId as a query param, preserve LOB context
    const basePath = url.split('?')[0];
    const lobContextRoutes = ['/simulator', '/run-algorithm', '/validation', '/version-compare'];
    if (lobContextRoutes.includes(basePath)) {
      const qMatch = url.match(/[?&]algorithmId=([^&\s#]+)/);
      if (qMatch) {
        this.currentLobId.set(qMatch[1]);
        return;
      }
    }

    this.currentLobId.set(null);
  }
}
